import React, { memo, useCallback, useMemo, useState } from 'react'
import { ControllerRenderProps } from 'react-hook-form'
import { isEmpty } from 'lodash'
import get from 'lodash/get'
import AutocompleteBase from 'transfix-ui/components/AutocompleteBase'
import { useAutocompleteQuery } from 'transfix-ui/components/AutocompleteBase/hooks/useAutocompleteQuery'
import { TAutocompleteBaseChangeHandler } from 'transfix-ui/components/AutocompleteBase/types'
import { IGetAutocompleteOptionsConfig } from 'transfix-ui/components/AutocompleteBase/utils/getAutocompleteOptions'
import { AutocompleteInputChangeReason } from 'transfix-ui/core'
import { IShipment } from 'types/graphqlTypes'
import { DISPLAY } from 'constants/display'
import { ISearchShipmentsQueryVariables, useSearchShipmentsQuery } from './graphql/SearchShipments'
import { IShipmentResultFragment } from './graphql/ShipmentResultFragment'

type TLabelGenerator = (shipment: IShipmentResultFragment) => string

const generateShipmentOption = (shipment: IShipmentResultFragment, getText?: TLabelGenerator) => {
  const { firstPickup, id, lastDelivery } = shipment
  const { city: pickupCity, stateCode: pickupStateCode } = firstPickup || {}
  const { city: deliveryCity, stateCode: deliveryStateCode } = lastDelivery || {}
  return {
    id,
    text: getText
      ? getText(shipment)
      : `#${id} ${DISPLAY.empty} ${pickupCity}, ${pickupStateCode} ${DISPLAY.empty} ${deliveryCity}, ${deliveryStateCode} `,
    shipment,
  }
}

export interface IShipmentAutosuggestAutocompleteProps
  extends Omit<
    React.ComponentProps<typeof AutocompleteBase>,
    | 'options'
    | 'defaultValue'
    | 'onChange'
    | 'value'
    | 'autoHighlight'
    | 'autoSelect'
    | 'openOnFocus'
    | 'selectOnFocus'
  > {}

export interface IShipmentAutosuggestProps extends ControllerRenderProps {
  shipment?: IShipmentResultFragment | null
  filters?: Omit<ISearchShipmentsQueryVariables, 'searchTerm'>
  enableCreateShipment?: boolean
  onCreateShipment?: (id: string) => void
  onShipmentChange?: (shipment: IShipment | null) => void
  onClear?: () => void
  inputValue?: string
  onInputValueChange?: (input: string) => void
  optionLabelGenerator?: (shipment: IShipmentResultFragment) => string
  minimumSearchLength?: number
  AutocompleteProps?: IShipmentAutosuggestAutocompleteProps
}

export interface ICreateShipmentOption {
  id: string
  text: string
  inputValue: string
}

export const ShipmentAutosuggest = memo<IShipmentAutosuggestProps>(
  ({
    AutocompleteProps = {},
    shipment,
    enableCreateShipment = false,
    onCreateShipment,
    onShipmentChange,
    onChange,
    onClear,
    value,
    inputValue,
    filters = {},
    optionLabelGenerator,
    onInputValueChange,
    minimumSearchLength,
  }) => {
    const [searchValue, setSearchValue] = useState<string>('')

    const { options = [], loading } = useAutocompleteQuery(
      useSearchShipmentsQuery,
      {
        variables: {
          searchTerm: searchValue,
          ...filters,
        },
        skip: minimumSearchLength ? searchValue.length < minimumSearchLength : false,
      },
      {
        value: shipment ? generateShipmentOption(shipment, optionLabelGenerator) : null,
        getOptionsConfig: data => {
          // Generate group of matching shipments from returned result set
          const { shipments: shipmentsQuery } = data || {}
          const { nodes: shipments } = shipmentsQuery || {}

          const matchingShipments: IGetAutocompleteOptionsConfig<IShipmentResultFragment> = {
            options: (shipments || []) as IShipmentResultFragment[],
            groupIndex: 1,
            groupName: 'Shipments',
            mapOption: shipment => generateShipmentOption(shipment, optionLabelGenerator),
          }

          // Option for creating new shipment - Only shown if enableCreateShipment is true
          const createOption: IGetAutocompleteOptionsConfig<ICreateShipmentOption> = {
            options: [
              {
                id: 'new-shipment',
                text: `Create shipment with new MC/DOT ${inputValue}`,
                inputValue: inputValue || '',
              },
            ],
            groupIndex: 0,
            optionType: 'withIcon',
            iconName: 'PlusIcon',
            dividerAfter: true,
            mapOption: option => option,
          }

          if (!isEmpty(inputValue) || !isEmpty(value) || !isEmpty(searchValue)) {
            return enableCreateShipment && Number(inputValue) > 0
              ? [createOption, matchingShipments]
              : matchingShipments
          }
          return []
        },
      }
    )

    const changeHandler: TAutocompleteBaseChangeHandler = useCallback(
      (_, selectedItem) => {
        const optionId = get(selectedItem, 'id')
        const isCreatingNewShipment = optionId === 'new-shipment'

        if (isCreatingNewShipment && onCreateShipment) {
          const newShipmentNumber = get(selectedItem, 'inputValue', '')
          onCreateShipment(newShipmentNumber)
        } else if (!isCreatingNewShipment) {
          onChange(optionId || '')
        }

        // If onShipmentChange function provided, send shipment object
        if (onShipmentChange) {
          const selectedShipment = get(selectedItem, 'shipment', null)
          onShipmentChange(selectedShipment)
        }
      },
      [onChange, onShipmentChange, onCreateShipment]
    )

    const inputChangeHandler = useCallback(
      (event: React.ChangeEvent<{}>, value: string, reason: AutocompleteInputChangeReason) => {
        const hasValidValue = event || (!event && value.length)
        const isInputUpdate = value !== '' && reason === 'input'
        const isEmptyValue = value === ''

        if (hasValidValue && onInputValueChange) {
          // Set input value - inputValue is used to generate 'Create new shipment option'
          onInputValueChange(value)
        }
        if (hasValidValue && isInputUpdate) {
          // Refresh result set with value for searchTerm
          setSearchValue(value)
        } else if (hasValidValue && isEmptyValue && onClear) {
          onClear()
        }
      },
      [onClear, onInputValueChange]
    )

    const computedValue = useMemo(() => {
      return options.find(({ id }) => id === value) || null
    }, [options, value])

    return (
      <AutocompleteBase
        {...AutocompleteProps}
        options={options}
        loading={loading}
        value={computedValue}
        onChange={changeHandler}
        inputValue={inputValue}
        onInputChange={inputChangeHandler}
        autoHighlight={false}
        autoSelect={false}
        openOnFocus={false}
        selectOnFocus={false}
      />
    )
  }
)

export default ShipmentAutosuggest
